import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useMaintenanceCheck } from "@/hooks/useMaintenanceCheck";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import About from "./pages/About";
import Features from "./pages/Features";
import Contact from "./pages/Contact";
import Dashboard from "./pages/Dashboard";
import ChairDashboard from "./pages/ChairDashboard";
import TreasurerDashboard from "./pages/TreasurerDashboard";
import SecretaryDashboard from "./pages/SecretaryDashboard";
import TrusteeDashboard from "./pages/TrusteeDashboard";
import AssetsDashboard from "./pages/AssetsDashboard";
import CateringDashboard from "./pages/CateringDashboard";
import EventsDashboard from "./pages/EventsDashboard";
import CommunicationsDashboard from "./pages/CommunicationsDashboard";
import CommunityDashboard from "./pages/CommunityDashboard";
import SchoolLiaisonDashboard from "./pages/SchoolLiaisonDashboard";
import ShopDashboard from "./pages/ShopDashboard";
import Settings from "./pages/Settings";
import Library from "./pages/Library";
import Forum from "./pages/Forum";
import Shop from "./pages/Shop";
import Marketplace from "./pages/Marketplace";
import CreateListing from "./pages/CreateListing";
import BuyCredits from "./pages/BuyCredits";
import MyListings from "./pages/MyListings";
import FundraisingPage from "./pages/FundraisingPage";
import Documents from "./pages/Documents";
import Members from "./pages/Members";
import Pricing from "./pages/Pricing";
import PricingNew from "./pages/PricingNew";
import Support from "./pages/Support";
import Games from "./pages/Games";
import RaffleGame from "./pages/RaffleGame";
import GuessingGame from "./pages/GuessingGame";
import BingoGame from "./pages/BingoGame";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import Refund from "./pages/Refund";
import NotFound from "./pages/NotFound";
import Maintenance from "./pages/Maintenance";
import AdminLogin from "./pages/AdminLogin";
import PTAAdmin from "./pages/PTAAdmin";
import SiteAdmin from "./pages/SiteAdmin";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import StaffSubmission from "./pages/StaffSubmission";
import Changelog from "./pages/Changelog";
import ParentPortal from "./pages/ParentPortal";
import Sponsorship from "./pages/Sponsorship";
import Integrations from "./pages/Integrations";
import Partners from "./pages/Partners";
import StallApplication from "./pages/StallApplication";

const queryClient = new QueryClient();

const Layout = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-screen flex-col">
    <Header />
    <main className="flex-1 container py-6">
      {children}
    </main>
    <Footer />
    <CookieBanner />
  </div>
);

const App = () => {
  const { showMaintenance, loading } = useMaintenanceCheck();

  if (loading) {
    return null; // or a loading spinner
  }

  if (showMaintenance) {
    return (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="*" element={<Maintenance />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
        <Route path="/" element={<Layout><Index /></Layout>} />
        <Route path="/auth" element={<Layout><Auth /></Layout>} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/about" element={<Layout><About /></Layout>} />
        <Route path="/features" element={<Layout><Features /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
        <Route path="/dashboard/chair" element={<Layout><ChairDashboard /></Layout>} />
        <Route path="/dashboard/treasurer" element={<Layout><TreasurerDashboard /></Layout>} />
        <Route path="/dashboard/secretary" element={<Layout><SecretaryDashboard /></Layout>} />
        <Route path="/dashboard/trustee" element={<Layout><TrusteeDashboard /></Layout>} />
        <Route path="/dashboard/assets" element={<Layout><AssetsDashboard /></Layout>} />
        <Route path="/dashboard/catering" element={<Layout><CateringDashboard /></Layout>} />
        <Route path="/dashboard/events" element={<Layout><EventsDashboard /></Layout>} />
        <Route path="/dashboard/communications" element={<Layout><CommunicationsDashboard /></Layout>} />
        <Route path="/dashboard/community" element={<Layout><CommunityDashboard /></Layout>} />
        <Route path="/dashboard/school-liaison" element={<Layout><SchoolLiaisonDashboard /></Layout>} />
        <Route path="/dashboard/shop" element={<ProtectedRoute><Layout><ShopDashboard /></Layout></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requireRole="admin"><Layout><PTAAdmin /></Layout></ProtectedRoute>} />
        <Route path="/site-admin" element={<ProtectedRoute requireRole="site_admin"><Layout><SiteAdmin /></Layout></ProtectedRoute>} />
        <Route path="/settings" element={<Layout><Settings /></Layout>} />
        <Route path="/library" element={<Layout><Library /></Layout>} />
        <Route path="/forum" element={<Layout><Forum /></Layout>} />
        <Route path="/shop" element={<Layout><Shop /></Layout>} />
        <Route path="/marketplace" element={<Layout><Marketplace /></Layout>} />
        <Route path="/marketplace/create" element={<ProtectedRoute><Layout><CreateListing /></Layout></ProtectedRoute>} />
        <Route path="/marketplace/buy-credits" element={<ProtectedRoute><Layout><BuyCredits /></Layout></ProtectedRoute>} />
        <Route path="/marketplace/my-listings" element={<ProtectedRoute><Layout><MyListings /></Layout></ProtectedRoute>} />
        <Route path="/fundraising" element={<FundraisingPage />} />
        <Route path="/documents" element={<Layout><Documents /></Layout>} />
        <Route path="/members" element={<Layout><Members /></Layout>} />
        <Route path="/pricing" element={<Layout><PricingNew /></Layout>} />
        <Route path="/pricing-old" element={<Layout><Pricing /></Layout>} />
        <Route path="/support" element={<Layout><Support /></Layout>} />
        <Route path="/games" element={<Layout><Games /></Layout>} />
        <Route path="/games/raffle" element={<Layout><RaffleGame /></Layout>} />
        <Route path="/games/guessing" element={<Layout><GuessingGame /></Layout>} />
        <Route path="/games/bingo" element={<Layout><BingoGame /></Layout>} />
        <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
        <Route path="/terms" element={<Layout><Terms /></Layout>} />
        <Route path="/cookies" element={<Layout><Cookies /></Layout>} />
        <Route path="/refund" element={<Layout><Refund /></Layout>} />
        <Route path="/blog" element={<Layout><Blog /></Layout>} />
        <Route path="/blog/:id" element={<Layout><BlogPost /></Layout>} />
        <Route path="/changelog" element={<Layout><Changelog /></Layout>} />
        <Route path="/portal" element={<ParentPortal />} />
        <Route path="/sponsorship" element={<Layout><Sponsorship /></Layout>} />
        <Route path="/integrations" element={<Layout><Integrations /></Layout>} />
        <Route path="/partners" element={<Layout><Partners /></Layout>} />
        <Route path="/staff-request/:ptaId" element={<StaffSubmission />} />
        <Route path="/stall-application/:ptaId" element={<StallApplication />} />
        <Route path="/fundraising/:ptaId" element={<FundraisingPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  </QueryClientProvider>
);
};

export default App;
